import { ReactNode } from 'react';
import { DateRange } from 'react-day-picker';

import { TimeWindowSelector } from '@/components//TimeWindowSelector';
import { DateRangePicker } from '@/components/DateRangePicker';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { TimeWindow } from '@/types';

export const LoadingSpinner = () => (
  <div className="flex h-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-800 border-t-transparent" />
  </div>
);

export const EmptyState = ({
  message,
  hint = 'Încercați să selectați o altă fereastră de timp.',
}: {
  message: string;
  hint?: string | null;
}) => (
  <div className="flex h-full flex-col items-center justify-center text-center">
    <p className="rounded-md bg-neutral-100 px-4 py-2 font-medium text-neutral-500 shadow-sm">
      {message}
    </p>
    {hint ? <p className="mt-2 text-xs text-neutral-400">{hint}</p> : null}
  </div>
);

interface ChartHeaderProps {
  title: string;
  description: string;
  timeWindow: TimeWindow;
  onTimeWindowChange: (window: TimeWindow) => void;
  labels: Record<TimeWindow, string>;
  showDateRange?: boolean;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  onClearDateRange?: () => void;
  dateRangePlaceholder?: string;
  dateRangeActiveHint?: string;
  extraControls?: ReactNode;
  showTimeWindowSelector?: boolean;
}

export const ChartHeader = ({
  title,
  description,
  timeWindow,
  onTimeWindowChange,
  labels,
  showDateRange = true,
  dateRange,
  onDateRangeChange,
  onClearDateRange,
  dateRangePlaceholder,
  dateRangeActiveHint,
  extraControls,
  showTimeWindowSelector = true,
}: ChartHeaderProps) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      <div className="flex flex-col gap-2 sm:items-end">
        {showTimeWindowSelector && (
          <TimeWindowSelector
            selectedTimeWindow={timeWindow}
            onTimeWindowChange={onTimeWindowChange}
            labels={labels}
          />
        )}
        {showDateRange && (
          <div className="flex w-full flex-col items-start">
            <DateRangePicker
              dateRange={dateRange}
              onChange={onDateRangeChange}
              onClear={onClearDateRange}
              placeholder={dateRangePlaceholder}
            />
            {dateRange?.from && dateRangeActiveHint && (
              <p className="ml-1 mt-1 rounded border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs italic text-neutral-600">
                {dateRangeActiveHint}
              </p>
            )}
          </div>
        )}
        {extraControls}
      </div>
    </div>
  </div>
);
